/**
 * Player Equipment Manager
 * RPG-style character screen with equipment slots and inventory
 */

import { useState } from 'react';
import { useGameStore } from '@/state';
import { EquipmentSlot } from '@/types';
import { EQUIPMENT_DATA } from '@/data';
import { SLOT_CONFIG, getActiveSlots } from '@/data/equipment/slotConfig';
import { equipmentManager } from '@/systems';
import { EquipmentSlotButton } from './EquipmentSlotButton';

/** CSS position classes for silhouette slot placement */
export const SLOT_POSITIONS: Record<string, string> = {
  'top': 'absolute top-2 left-1/2 -translate-x-1/2',
  'left': 'absolute left-2 top-1/3 -translate-y-1/2',
  'right': 'absolute right-2 top-1/3 -translate-y-1/2',
  'bottom-left': 'absolute left-4 bottom-4',
  'bottom-right': 'absolute right-4 bottom-4',
};

export function PlayerEquipmentManager() {
  const equippedGear = useGameStore((state) => state.playerState.equippedGear);
  const ownedEquipment = useGameStore((state) => state.playerState.ownedEquipment);
  const playerStats = useGameStore((state) => state.playerStats);
  const [selectedSlot, setSelectedSlot] = useState<EquipmentSlot | null>(null);

  const handleSlotClick = (slot: EquipmentSlot) => {
    setSelectedSlot(selectedSlot === slot ? null : slot);
  };

  const handleEquipItem = (equipmentId: string) => {
    if (!selectedSlot) return;

    const equipment = EQUIPMENT_DATA[equipmentId];
    if (equipment.slot === selectedSlot) {
      equipmentManager.equipItem(equipmentId);
      setSelectedSlot(null);
    }
  };

  const handleUnequip = (slot: EquipmentSlot) => {
    equipmentManager.unequipItem(slot);
    setSelectedSlot(null);
  };

  // Get equipped item for a slot
  const getEquippedItem = (slot: EquipmentSlot) => {
    const itemId = equippedGear[slot];
    return itemId ? EQUIPMENT_DATA[itemId] : null;
  };

  // Group owned equipment by slot using centralized config (pass equippedGear for conditional slots)
  const activeSlots = getActiveSlots(equippedGear);
  const availableQty = equipmentManager.getAvailableQuantities();
  const groupedItems = activeSlots.map((slot) => {
    const meta = SLOT_CONFIG[slot];
    const items = Object.keys(ownedEquipment)
      .filter((id) => EQUIPMENT_DATA[id]?.slot === slot)
      .map((id) => ({
        id,
        definition: EQUIPMENT_DATA[id],
        data: ownedEquipment[id],
        availableCount: availableQty[id] || 0,
      }));
    return { slot, meta, items };
  }).filter((group) => group.items.length > 0);

  return (
    <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-700 space-y-6">
      <div>
        <h3 className="text-xl font-bold text-white mb-1">⚙️ Equipment</h3>
        <p className="text-xs text-gray-400">
          Click a slot to equip items from your inventory
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Character + Slots */}
        <div className="flex flex-col items-center space-y-6">
          {/* Stats Summary */}
          <div className="w-full bg-gray-700/50 rounded-lg p-4 border border-gray-600">
            <h4 className="text-sm font-bold text-gray-300 mb-2">Total Stats</h4>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="text-gray-400">Fishing Speed:</span>
                <span className="text-green-400 ml-1">+{Math.round(playerStats.fishingSpeed * 100)}%</span>
              </div>
              <div>
                <span className="text-gray-400">Catch Chance:</span>
                <span className="text-blue-400 ml-1">+{Math.round(playerStats.catchChance * 100)}%</span>
              </div>
              <div>
                <span className="text-gray-400">Quality Bonus:</span>
                <span className="text-purple-400 ml-1">+{Math.round(playerStats.qualityBonus * 100)}%</span>
              </div>
              <div>
                <span className="text-gray-400">Value Bonus:</span>
                <span className="text-yellow-400 ml-1">+{Math.round(playerStats.valueBonus * 100)}%</span>
              </div>
            </div>
          </div>

          {/* Character Silhouette + Slots */}
          <div className="relative w-64 h-80 bg-gray-700/30 rounded-lg border-2 border-gray-600 flex items-center justify-center">
            {/* Character Silhouette */}
            <div className="text-9xl opacity-20">🎣</div>

            {/* Data-driven equipment slots */}
            {activeSlots.map((slot) => {
              const meta = SLOT_CONFIG[slot];
              const posClass = SLOT_POSITIONS[meta.silhouettePosition] || '';
              return (
                <div key={slot} className={`${posClass} [&:hover]:z-50`}>
                  <EquipmentSlotButton
                    slot={slot}
                    equipped={getEquippedItem(slot)}
                    isSelected={selectedSlot === slot}
                    onClick={() => handleSlotClick(slot)}
                    onUnequip={() => handleUnequip(slot)}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* Right: Grouped Inventory */}
        <div className="space-y-3">
          <h4 className="text-sm font-bold text-gray-300">🎒 Inventory</h4>

          {groupedItems.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-sm">
              No equipment owned. Visit the shop to purchase gear!
            </div>
          ) : (
            <div className="max-h-96 overflow-y-auto space-y-4">
              {groupedItems.map(({ slot, meta, items }) => (
                <div key={slot}>
                  {/* Section Header */}
                  <div className="sticky top-0 bg-gray-800/90 backdrop-blur-sm px-2 py-1.5 rounded border-b border-gray-600 mb-2 z-10">
                    <span className="text-sm font-semibold text-gray-300">
                      {meta.icon} {meta.pluralLabel}
                      <span className="text-gray-500 ml-1 text-xs">({items.length})</span>
                    </span>
                  </div>

                  {/* Items in group */}
                  <div className="space-y-2">
                    {items.map((item) => {
                      const isCompatible = selectedSlot && item.definition.slot === selectedSlot;
                      const isEquipped = equippedGear[item.definition.slot] === item.id;
                      const canEquip = isCompatible && (item.availableCount > 0 || isEquipped);

                      return (
                        <button
                          key={item.id}
                          onClick={() => handleEquipItem(item.id)}
                          disabled={!canEquip && selectedSlot !== null}
                          className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                            canEquip
                              ? 'bg-blue-600/30 border-blue-500 hover:bg-blue-600/50 animate-pulse'
                              : selectedSlot
                              ? 'bg-gray-800/30 border-gray-700 opacity-30 cursor-not-allowed'
                              : 'bg-gray-700/50 border-gray-600 hover:border-gray-500 hover:bg-gray-700'
                          } ${isEquipped ? 'ring-2 ring-green-500' : ''}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-bold text-white">{item.definition.name}</span>
                                {isEquipped && <span className="text-xs text-green-400">✓ Equipped</span>}
                              </div>
                              <div className="text-xs text-gray-400 mt-1">
                                {item.definition.description || 'No description'}
                              </div>
                              <div className="flex items-center gap-2 mt-1">
                                <span className={`text-xs ${item.availableCount === 0 && !isEquipped ? 'text-red-400' : 'text-gray-500'}`}>
                                  {item.data.quantity > 1
                                    ? `Avail: ${isEquipped ? item.availableCount + 1 : item.availableCount}/${item.data.quantity}`
                                    : item.availableCount === 0 && !isEquipped
                                    ? 'In use by buddy'
                                    : `Qty: ${item.data.quantity}`}
                                </span>
                              </div>
                            </div>

                            {/* Stats */}
                            <div className="text-xs text-right space-y-1">
                              {item.definition.stats.fishingSpeed && (
                                <div className="text-green-400">Speed +{Math.round(item.definition.stats.fishingSpeed * 100)}%</div>
                              )}
                              {item.definition.stats.catchChance && (
                                <div className="text-blue-400">Catch +{Math.round(item.definition.stats.catchChance * 100)}%</div>
                              )}
                              {item.definition.stats.qualityBonus && (
                                <div className="text-purple-400">Quality +{Math.round(item.definition.stats.qualityBonus * 100)}%</div>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

